import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import type { RetentionCohort } from '@/lib/admin/use-executive-dashboard';

interface CohortHeatmapProps {
  cohorts: RetentionCohort[];
}

const WEEK_COLS = ['W1', 'W2', 'W3', 'W4'] as const;

function getCellColor(value: number): { bg: string; text: string } {
  if (value === 0) return { bg: 'transparent', text: 'tertiary' };
  if (value >= 50) return { bg: 'rgba(34, 197, 94, 0.15)', text: '#16A34A' };
  if (value >= 30) return { bg: 'rgba(245, 158, 11, 0.15)', text: '#D97706' };
  return { bg: 'rgba(239, 68, 68, 0.15)', text: '#DC2626' };
}

function formatWeekLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

export function CohortHeatmap({ cohorts }: CohortHeatmapProps) {
  const colors = useThemeColors();

  if (!cohorts.length) {
    return (
      <View className="items-center py-6">
        <Text style={{ color: colors.text.tertiary }} className="text-xs">No cohort data</Text>
      </View>
    );
  }

  const COL_W = 64;
  const LABEL_W = 72;
  const SIZE_W = 48;
  const ROW_H = 36;

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
      <View>
        {/* Header row */}
        <View className="flex-row items-center" style={{ height: ROW_H }}>
          <View style={{ width: LABEL_W }} className="justify-center px-2">
            <Text style={{ color: colors.text.tertiary }} className="text-[10px] font-semibold uppercase">
              Cohort
            </Text>
          </View>
          <View style={{ width: SIZE_W }} className="justify-center items-center">
            <Text style={{ color: colors.text.tertiary }} className="text-[10px] font-semibold uppercase">
              Size
            </Text>
          </View>
          {WEEK_COLS.map((w) => (
            <View key={w} style={{ width: COL_W }} className="justify-center items-center">
              <Text style={{ color: colors.text.tertiary }} className="text-[10px] font-semibold uppercase">
                {w}
              </Text>
            </View>
          ))}
        </View>

        {/* Divider */}
        <View style={{ height: 1, backgroundColor: colors.border.light }} />

        {/* Data rows */}
        {cohorts.map((cohort) => {
          const retentions = [cohort.w1_retention, cohort.w2_retention, cohort.w3_retention, cohort.w4_retention];

          return (
            <View
              key={cohort.cohort_week}
              className="flex-row items-center"
              style={{
                height: ROW_H,
                borderBottomWidth: 1,
                borderBottomColor: colors.border.light,
              }}
            >
              <View style={{ width: LABEL_W }} className="justify-center px-2">
                <Text style={{ color: colors.text.primary }} className="text-xs font-medium">
                  {formatWeekLabel(cohort.cohort_week)}
                </Text>
              </View>
              <View style={{ width: SIZE_W }} className="justify-center items-center">
                <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold">
                  {cohort.cohort_size}
                </Text>
              </View>
              {retentions.map((val, i) => {
                const { bg, text } = getCellColor(val);
                return (
                  <View
                    key={`cell-${cohort.cohort_week}-${i}`}
                    style={{ width: COL_W }}
                    className="justify-center items-center"
                  >
                    <View
                      className="rounded-md items-center justify-center"
                      style={{
                        backgroundColor: bg,
                        width: COL_W - 8,
                        height: ROW_H - 8,
                      }}
                    >
                      <Text
                        className="text-xs font-bold"
                        style={{ color: val === 0 ? colors.text.tertiary : text }}
                      >
                        {val > 0 ? `${val}%` : '—'}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
}
